import type { Contract } from '../types';

export type UrgencyLevel = 'low' | 'medium' | 'high';
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MARKET_TIME_ZONE = 'America/New_York';

export const formatDate = (value?: string | null) => {
  if (!value || value === 'TBD') return 'TBD';
  try {
    if (DATE_ONLY_PATTERN.test(value)) {
      const [year, month, day] = value.split('-').map((part) => Number(part));
      return new Date(year, month - 1, day).toLocaleDateString();
    }

    return new Date(value).toLocaleDateString();
  } catch {
    return value ?? 'TBD';
  }
};

export const formatDueTime = (value?: string | null) => {
  if (!value || value === 'TBD') return null;
  try {
    if (DATE_ONLY_PATTERN.test(value)) {
      return null;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: MARKET_TIME_ZONE,
      timeZoneName: 'short',
    }).format(parsed);
  } catch {
    return null;
  }
};

export const formatDueDateTime = (contract: Pick<Contract, 'due_date' | 'due_at'>) => {
  const dateLabel = formatDate(contract.due_date);
  const timeLabel = formatDueTime(contract.due_at);
  if (dateLabel === 'TBD') {
    return dateLabel;
  }
  if (!timeLabel) {
    return dateLabel;
  }
  return `${dateLabel} at ${timeLabel}`;
};

export const getComparableDueValue = (contract: Pick<Contract, 'due_date' | 'due_at'>) => {
  return contract.due_at || contract.due_date;
};

export const getUrgencyLevel = (urgency?: string | null): UrgencyLevel => {
  if (urgency === 'high') return 'high';
  if (urgency === 'medium') return 'medium';
  return 'low';
};

export const urgencyBadgeClasses: Record<UrgencyLevel, string> = {
  high: 'bg-rose-100 text-rose-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-emerald-100 text-emerald-700',
};

export const urgencyDotClasses: Record<UrgencyLevel, string> = {
  high: 'bg-rose-500',
  medium: 'bg-amber-500',
  low: 'bg-emerald-500',
};

export const formatDueSummary = (contract: Contract) => {
  const days = contract.days_until_due;
  if (days === undefined || days === null) return null;
  if (days < 0) return 'Overdue';
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `Due in ${days} days`;
};

export const contractColumns = [
  { key: 'title', label: 'Title' },
  { key: 'industry', label: 'Industry' },
  { key: 'city', label: 'Procuring Organization' },
  { key: 'due_date', label: 'Due' },
];
